import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { VILLES } from 'app/classesTs/Ville';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import Swal from 'sweetalert2';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-liste-agence-mob',
  templateUrl: './liste-agence-mob.component.html',
  styleUrls: ['./liste-agence-mob.component.scss']
})
export class ListeAgenceMobComponent implements OnInit, OnDestroy {
  // Agency Data
  allAgencies: any[] = [];
  filteredAgencies: any[] = [];
  
  // Filtering & Search
  selectedVille: string = 'all';
  searchTerm: string = '';
  villes = VILLES;
  
  // Pagination with URL persistence
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  
  // Loading State - REMOVED SPINNER LOADING
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // Router subscription
  private routerSubscription: Subscription;
  
  // Data loaded flag
  dataLoaded: boolean = false;

  constructor(
    private agenciesService: AgenciesService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {
    // Listen to route changes to restore state
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.restoreStateFromUrl();
      });
  }

  ngOnInit(): void {
    // First restore state from URL, then load data
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      this.restoreStateFromUrl();
      this.loadAgencies();
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Restore state from URL parameters - FIXED TO HANDLE BACK NAVIGATION
   */
  private restoreStateFromUrl(): void {
    this.route.queryParams.subscribe(params => {
      // Restore page number (default to 1 if not provided)
      this.currentPage = params['page'] ? +params['page'] : 1;
      
      // Restore ville filter (default to 'all')
      this.selectedVille = params['ville'] || 'all';
      
      // Restore search term (default to empty)
      this.searchTerm = params['search'] || '';
      
      console.log('Restored state from URL:', {
        page: this.currentPage,
        ville: this.selectedVille,
        search: this.searchTerm
      });
    });
  }

  /**
   * Update URL with current state - FIXED TO MAINTAIN STATE
   */
  private updateUrlState(): void {
    const queryParams: any = {};
    
    // Always include page if not 1
    if (this.currentPage !== 1) {
      queryParams['page'] = this.currentPage;
    } else {
      queryParams['page'] = null; // Remove page param if it's 1
    }
    
    // Always include ville if not 'all'
    if (this.selectedVille !== 'all') {
      queryParams['ville'] = this.selectedVille;
    } else {
      queryParams['ville'] = null; // Remove ville param if it's 'all'
    }
    
    // Include search term if not empty
    if (this.searchTerm.trim()) {
      queryParams['search'] = this.searchTerm;
    } else {
      queryParams['search'] = null; // Remove search param if empty
    }

    // Create URL tree with query params
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge' // Merge with existing params
    });

    // Update URL without triggering navigation
    this.location.replaceState(urlTree.toString());
    
    console.log('Updated URL state:', queryParams);
  }

  /**
   * Load all agencies from API
   */
  loadAgencies(): void {
    // Set loading state but don't show spinner
    this.isLoading = true;
    this.errorMessage = '';
    
    // Note: We're not using localStorage for data storage
    // All data comes from API
    
    this.agenciesService.getListAgenceImmob().subscribe({
      next: (response: any) => {
        this.handleApiResponse(response);
      },
      error: (error) => {
        this.handleApiError(error);
      }
    });
  }

  /**
   * Load agencies by ville using the specific API endpoint
   */
  loadAgenciesByVille(villeId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.selectedVille = villeId;
    this.currentPage = 1; // Reset to page 1 when changing ville
    
    // Update URL immediately when filter changes
    this.updateUrlState();
    
    if (villeId === 'all') {
      this.agenciesService.getListAgenceImmob().subscribe({
        next: (response: any) => {
          this.handleApiResponse(response);
        },
        error: (error) => {
          this.handleApiError(error);
        }
      });
    } else {
      this.agenciesService.getListAgenceParVille(villeId).subscribe({
        next: (response: any) => {
          this.handleApiResponse(response);
        },
        error: (error) => {
          this.handleApiError(error);
        }
      });
    }
  }

  /**
   * Handle API response
   */
  private handleApiResponse(response: any): void {
    try {
      let agenciesData: any[] = [];
      
      if (Array.isArray(response)) {
        agenciesData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        agenciesData = response.data;
      } else if (response && typeof response === 'object') {
        const arrayValues = Object.values(response).filter(val => Array.isArray(val));
        agenciesData = arrayValues.length > 0 ? arrayValues[0] as any[] : [];
      }
      
      // Sort agencies: those with biens first, then those without
      agenciesData.sort((a: any, b: any) => {
        const aHasBiens = a.biens && Array.isArray(a.biens) && a.biens.length > 0;
        const bHasBiens = b.biens && Array.isArray(b.biens) && b.biens.length > 0;
        
        if (aHasBiens && !bHasBiens) return -1;
        if (!aHasBiens && bHasBiens) return 1;
        return 0;
      });
      
      this.allAgencies = agenciesData.map(item => this.transformAgencyData(item));
      this.applyFilters();
      
      this.isLoading = false;
      this.dataLoaded = true;
      
      console.log('Data loaded successfully:', this.allAgencies.length, 'agencies');
    } catch (error) {
      console.error('Error processing response:', error);
      this.errorMessage = 'Erreur lors du traitement des données';
      this.allAgencies = [];
      this.applyFilters();
      this.isLoading = false;
      this.dataLoaded = true;
    }
  }

  /**
   * Handle API error
   */
  private handleApiError(error: any): void {
    console.error('Error loading agencies:', error);
    this.errorMessage = 'Erreur lors du chargement des agences';
    this.isLoading = false;
    this.dataLoaded = true;
    this.allAgencies = [];
    this.applyFilters();
  }

  /**
   * Transform agency data
   */
  private transformAgencyData(item: any): any {
    return {
      id: item.id || 0,
      name: item.name || 'Sans nom',
      description: item.description || '',
      adresse: item.adresse || '',
      region: (item.region || '').toLowerCase(),
      role: item.role || 'Agence Immobilière',
      phone_number: item.phone_number || '',
      phone_number2: item.phone_number2 || '',
      phone_number_commercial: item.phone_number_commercial || '',
      email: item.email || '',
      logo_url: item.logo_url || '',
      cover_url: item.cover_url || '',
      website: item.website || '',
      facebook: item.facebook || '',
      instagram: item.instagram || '',
      linkedin: item.linkedin || '',
      google_map_url: item.google_map_url || '',
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || '',
      publishedAt: item.publishedAt || '',
      biens: Array.isArray(item.biens) ? item.biens : [],
      newsletter_checked: item.newsletter_checked || false,
      newsletter_message: item.newsletter_message || '',
      google_business_url: item.google_business_url || '',
      chaine_youtube: item.chaine_youtube || '',
      accepte_publication: item.accepte_publication || false,
      public_contact_phone: item.public_contact_phone || '',
      public_contact_email: item.public_contact_email || '',
      publicShareMessage: item.publicShareMessage || ''
    };
  }

  /**
   * Get count for a specific ville
   */
  getVilleCount(villeId: string): number {
    if (villeId === 'all') return this.allAgencies.length;
    
    const ville = this.villes.find(v => v.id === villeId);
    if (!ville) return 0;
    
    return this.allAgencies.filter(agency => {
      const agencyRegion = agency.region || '';
      return ville.values.some(value => 
        value.toLowerCase() === agencyRegion.toLowerCase()
      );
    }).length;
  }

  /**
   * Get total biens count
   */
  getTotalBiens(): number {
    return this.allAgencies.reduce((total, agency) => {
      return total + (agency.biens?.length || 0);
    }, 0);
  }

  /**
   * Apply filters (search only)
   */
  applyFilters(): void {
    let filtered = [...this.allAgencies];
    
    // Filter by search term
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(agency => {
        const nameMatch = agency.name?.toLowerCase().includes(searchLower) || false;
        const addressMatch = agency.adresse?.toLowerCase().includes(searchLower) || false;
        const emailMatch = agency.email?.toLowerCase().includes(searchLower) || false;
        const phoneMatch = agency.phone_number?.toString().includes(searchLower) || false;
        const roleMatch = agency.role?.toLowerCase().includes(searchLower) || false;
        return nameMatch || addressMatch || emailMatch || phoneMatch || roleMatch;
      });
    }
    
    this.filteredAgencies = filtered;
    this.totalItems = filtered.length;
    
    // Ensure current page is valid
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage);
    if (this.currentPage > maxPage && maxPage > 0) {
      this.currentPage = maxPage;
      this.updateUrlState();
    }
  }

  /**
   * Perform search
   */
  performSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.selectedVille = 'all';
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadAgencies();
    this.updateUrlState();
  }

  /**
   * Get ville label from region or id
   */
  getVilleLabel(regionOrId: string): string {
    if (!regionOrId) return 'Non spécifié';
    
    // Check if it's a ville id
    const villeById = this.villes.find(v => v.id === regionOrId);
    if (villeById) return villeById.label;
    
    // Check if it's a region
    const villeByRegion = this.villes.find(v => 
      v.values.some(value => value.toLowerCase() === regionOrId.toLowerCase())
    );
    
    return villeByRegion ? villeByRegion.label : 
      regionOrId.charAt(0).toUpperCase() + regionOrId.slice(1);
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.updateUrlState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Get display range for pagination info
   */
  getDisplayRange(): { start: number, end: number } {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    return { start, end };
  }

  /**
   * Handle image error
   */
  handleImageError(event: any): void {
    event.target.src = 'assets/default-logo.png';
  }

  /**
   * Delete agency with confirmation
   */
  deleteAgency(agency: any): void {
    if (!agency.id || !agency.name) return;
    
    Swal.fire({
      title: `Supprimer ${agency.name}?`,
      text: "Cette action est irréversible et supprimera également tous les biens associés.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4757",
      cancelButtonColor: "#2ed573",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        this.agenciesService.deleteItem(agency.id).subscribe({
          next: () => {
            Swal.fire({
              title: "Supprimé!",
              text: "L'agence a été supprimée avec succès.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
            
            // Reload data based on current filter
            if (this.selectedVille === 'all') {
              this.loadAgencies();
            } else {
              this.loadAgenciesByVille(this.selectedVille);
            }
          },
          error: (error) => {
            console.error('Error deleting agency:', error);
            Swal.fire({
              title: "Erreur!",
              text: "Impossible de supprimer l'agence. Veuillez réessayer.",
              icon: "error"
            });
          }
        });
      }
    });
  }
}